/// <amd-module name="Build/Admin/Common/Sources/AgentPoolSource" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { ResourceLimit, TaskAgent, TaskAgentPool, TaskAgentPoolStatus, TaskAgentPoolMaintenanceDefinition } from "DistributedTask/Client/Contracts/TaskAgent";
import { getTaskAgentClient, ITaskAgentRestClient } from "DistributedTask/Client/RestClient/TaskAgent";

import { TaskAgentPoolsData, TaskAgentPoolData, TimeZoneData } from "Build/Admin/Common/Client/Contracts/DistributedTask/DataProvider";
import { DataProviders } from "Build/Admin/Common/Constants";

export class AgentPoolSource {
    private _client: ITaskAgentRestClient;
    private _pageContext: IVssPageContext;
    private _poolsData: TaskAgentPoolsData | undefined;
    private _poolsDataInitialized: boolean;
    private _poolData: TaskAgentPoolData | undefined;
    private _poolDataInitialized: boolean;

    constructor(pageContext: IVssPageContext) {
        this._client = getTaskAgentClient(pageContext);
        this._pageContext = pageContext;
    }

    public getCachedAgentPools(): TaskAgentPool[] {
        this._initializeAgentPoolsData();
        if (this._poolsData && this._poolsData.taskAgentPools) {
            return this._poolsData.taskAgentPools;
        }
        else {
            return [];
        }
    }

    public getCachedAgentPoolStatuses(): TaskAgentPoolStatus[] {
        this._initializeAgentPoolsData();
        if (this._poolsData && this._poolsData.taskAgentPoolStatusList) {
            return this._poolsData.taskAgentPoolStatusList;
        }
        else {
            return [];
        }
    }

    public getCachedSelectedAgentPool(): TaskAgentPool | undefined {
        this._initializeAgentPoolData();
        if (this._poolData && this._poolData.selectedAgentPool) {
            return this._poolData.selectedAgentPool;
        }
        else {
            return undefined;
        }
    }

    public getCachedValidTimezones(): TimeZoneData[] {
        this._initializeAgentPoolData();
        if (this._poolData && this._poolData.validTimeZones) {
            return this._poolData.validTimeZones;
        }
        else {
            return [];
        }
    }

    public getCachedAgentPoolAgents(): TaskAgent[] {
        this._initializeAgentPoolData();
        if (this._poolData && this._poolData.agents) {
            return this._poolData.agents;
        }
        else {
            return [];
        }
    }

    public async addAgentPool(pool: TaskAgentPool): Promise<TaskAgentPool> {
        return this._client.addAgentPool(pool);
    }

    public async deleteAgentPool(pool: TaskAgentPool): Promise<void> {
        return this._client.deleteAgentPool(pool.id);
    }

    public async updateAgentPool(pool: TaskAgentPool): Promise<TaskAgentPool> {
        return this._client.updateAgentPool(pool, pool.id);
    }

    public async getAgentPoolMetadata(taskAgentPoolId: number): Promise<string> {
        return this._client.getAgentPoolMetadata(taskAgentPoolId);
    }

    public async setAgentPoolMetadata(taskAgentPoolId: number, agentPoolMetadata: string): Promise<void> {
        return this._client.setAgentPoolMetadata(agentPoolMetadata, taskAgentPoolId);
    }

    public async refreshAgents(poolId: number): Promise<void> {
        return this._client.refreshAgents(poolId);
    }

    public async getAgentPool(taskAgentPoolId: number): Promise<TaskAgentPool> {
        return this._client.getAgentPool(taskAgentPoolId);
    }

    public async getAgentPools(): Promise<TaskAgentPool[]> {
        return this._client.getAgentPools();
    }

    public async getMaintenanceDefinitions(poolId: number): Promise<TaskAgentPoolMaintenanceDefinition[]> {
        return this._client.getAgentPoolMaintenanceDefinitions(poolId);
    }

    public async createMaintenanceDefinition(definition: TaskAgentPoolMaintenanceDefinition, poolId: number): Promise<TaskAgentPoolMaintenanceDefinition> {
        return this._client.createAgentPoolMaintenanceDefinition(definition, poolId);
    }

    public async updateMaintenanceDefinition(definition: TaskAgentPoolMaintenanceDefinition, poolId: number): Promise<TaskAgentPoolMaintenanceDefinition> {
        return this._client.updateAgentPoolMaintenanceDefinition(definition, poolId, definition.id);
    }

    public async getAgentPoolStatusAsync(taskAgentPoolId: number): Promise<TaskAgentPoolsData | undefined> {
        const parameters = {
            poolId: taskAgentPoolId,
            mode: "status",
        };

        const contributionService: IVssContributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<TaskAgentPoolsData>(DataProviders.Pools, parameters, true);
    }

    public async getResourceLimitsAsync(): Promise<ResourceLimit[]> {
        return this._client.getResourceLimits();
    }

    private _initializeAgentPoolsData(): void {
        if (!this._poolsDataInitialized) {
            const contributionService: IVssContributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            this._poolsData = contributionService.getData<TaskAgentPoolsData>(DataProviders.Pools);
            this._poolsDataInitialized = true;
        }
    }

    private _initializeAgentPoolData(): void {
        if (!this._poolDataInitialized) {
            const contributionService: IVssContributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            this._poolData = contributionService.getData<TaskAgentPoolData>(DataProviders.Pool);
            this._poolDataInitialized = true;
        }
    }
}
